<template>
  <vs-popup title="Desfazer Encerramento em Massa" :active.sync="isActive" @close="close()">
    <div class="d-flex flex-column" style="gap:16px; padding:10px;">
      <div v-if="currentStep === 1" class="step-content">
        <p class="step-description">Escolha o período em que os encerramentos foram realizados.</p>

        <div class="d-flex" style="align-items:center; gap:16px;">
          <div style="flex:1">
            <label style="font-size: 13px; font-weight: 500; margin-bottom: 8px; display: block;">
              Data Inicial
            </label>
            <flat-pickr
              :config="datetimepickr"
              v-model="startAt"
              placeholder="Data e hora inicial"
              style="width:100%; border-radius:6px; padding:8px 10px;"
            />
          </div>

          <div style="flex:1">
            <label style="font-size: 13px; font-weight: 500; margin-bottom: 8px; display: block;">
              Data Final
            </label>
            <flat-pickr
              :config="finishAtConfig(datetimepickr)"
              v-model="finishAt"
              placeholder="Data e hora final"
              :disabled="startAt === '' || startAt === null"
              style="width:100%; border-radius:6px; padding:8px 10px;"
            />
          </div>
        </div>

        <div class="d-flex justify-end" style="margin-top: 16px;">
          <vs-button type="filled" @click="nextStep" :disabled="!startAt || !finishAt || startAt === '' || finishAt === ''">
            Próximo
          </vs-button>
        </div>
      </div>

      <div v-if="currentStep === 2" class="step-content">
        <p class="step-description">Escolha o usuário que realizou os encerramentos no período selecionado.</p>

        <div class="period-summary">
          <p><strong>Período selecionado:</strong> {{ formatDate(startAt) }} - {{ formatDate(finishAt) }}</p>
        </div>

        <div>
          <label style="font-size: 13px; font-weight: 500; margin-bottom: 8px; display: block;">
            Usuário
          </label>
          <vs-select
            v-model="selectedUserId"
            placeholder="Escolha um usuário"
            style="width:100%;"
            :disabled="loadingUsers"
          >
            <vs-select-item
              :key="user.user_id"
              :value="user.user_id"
              :text="`${user.user_name} (${user.total_finished} encerramentos)`"
              v-for="user in users"
            />
          </vs-select>
        </div>

        <div v-if="!users.length && !loadingUsers" class="info-box">
          <p>Para desfazer encerramentos, primeiro identifique os usuários através do módulo "Identificar usuário que fez Encerramento em Massa".</p>
        </div>

        <div v-if="loadingUsers" class="d-flex justify-center" style="padding: 20px;">
          <vs-progress indeterminate color="primary">Carregando usuários...</vs-progress>
        </div>

        <div class="d-flex justify-between" style="margin-top: 16px;">
          <vs-button type="border" @click="prevStep">
            Voltar
          </vs-button>
          <vs-button type="filled" @click="nextStep" :disabled="!selectedUserId || loadingUsers">
            Próximo
          </vs-button>
        </div>
      </div>

      <div v-if="currentStep === 3" class="step-content">
        <p class="step-description">Confirme os dados antes de prosseguir com a operação.</p>

        <div class="confirmation-summary">
          <div class="summary-item">
            <strong>Período:</strong> {{ formatDate(startAt) }} - {{ formatDate(finishAt) }}
          </div>
          <div class="summary-item">
            <strong>Usuário:</strong> {{ selectedUser ? selectedUser.user_name : 'N/A' }}
          </div>
          <div class="summary-item">
            <strong>Total de Encerramentos:</strong> {{ selectedUser ? selectedUser.total_finished : 0 }}
          </div>
        </div>

        <div class="warning-box">
          <p><strong>Atenção:</strong> Esta ação irá reabrir todos os atendimentos encerrados pelo usuário selecionado no período informado. Esta operação não pode ser desfeita.</p>
        </div>

        <div v-if="loading" class="d-flex justify-center" style="padding: 20px;">
          <vs-progress indeterminate color="primary">Processando...</vs-progress>
        </div>

        <div v-if="result && !loading" class="result-box">
          <h4>Resultado da Operação</h4>
          <p><strong>Usuário:</strong> {{ result.user_name }}</p>
          <p><strong>Total de Atendimentos Reabertos:</strong> {{ result.total_updated }}</p>
          <p v-if="result.message"><strong>Mensagem:</strong> {{ result.message }}</p>
        </div>

        <div class="d-flex justify-between" v-if="!loading && !result">
          <vs-button type="border" @click="prevStep">
            Voltar
          </vs-button>
          <vs-button type="filled" color="danger" @click="confirmUndo">
            Desfazer Encerramentos
          </vs-button>
        </div>

        <div class="d-flex justify-end" v-if="result && !loading">
          <vs-button type="border" @click="clearResults">
            Fechar
          </vs-button>
        </div>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import {mapGetters} from 'vuex';
import 'flatpickr/dist/flatpickr.css';
import flatPickr from 'vue-flatpickr-component';
import {Portuguese} from 'flatpickr/dist/l10n/pt.js';

export default {
  name: 'UndoFinishedTickets',
  components: {flatPickr},
  props: {
    isActive: {
      type: Boolean,
      default: false
    },
    tenantName: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapGetters(['token']),
    selectedUser() {
      return this.users.find(u => u.user_id === this.selectedUserId);
    }
  },
  data() {
    return {
      currentStep: 1,
      selectedUserId: null,
      startAt: '',
      finishAt: '',
      users: [],
      loadingUsers: false,
      result: null,
      loading: false,
      operationData: null,
      datetimepickr: {
        enableTime: true,
        noCalendar: false,
        dateFormat: "Y-m-d H:i:S",
        time_24hr: true,
        locale: Portuguese
      }
    }
  },
  methods: {
    close() {
      this.currentStep = 1;
      this.selectedUserId = null;
      this.startAt = '';
      this.finishAt = '';
      this.users = [];
      this.result = null;
      this.loading = false;
      this.loadingUsers = false;
      this.$emit('isActiveFalse');
    },
    nextStep() {
      if (this.currentStep === 1 && this.startAt && this.finishAt) {
        this.currentStep = 2;
        this.loadUsers();
      } else if (this.currentStep === 2 && this.selectedUserId) {
        this.currentStep = 3;
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    finishAtConfig(config) {
      return {
        ...config,
        minDate: this.startAt
      }
    },
    clearResults() {
      this.currentStep = 1;
      this.result = null;
      this.selectedUserId = null;
      this.startAt = '';
      this.finishAt = '';
      this.users = [];
      this.operationData = null;
    },
    reopenModal() {
      this.operationData = null;
      this.isActive = true;
    },
    loadUsers() {
      if (!this.startAt || !this.finishAt) return;
      
      this.loadingUsers = true;
      this.$store.dispatch('getFinishedTicketsRank', {
        from: this.startAt,
        to: this.finishAt,
        tenantName: this.tenantName,
        token: this.token
      }).then(response => {
        this.users = response.data || [];
        this.loadingUsers = false;
      }).catch(exception => {
        console.error(exception);
        this.loadingUsers = false;
        this.$vs.notify({
          color: 'danger',
          title: 'Erro!',
          text: 'Ocorreu um erro ao carregar os usuários! Entre em contato com o time técnico.'
        });
      });
    },
    confirmUndo() {
      this.operationData = {
        selectedUserId: this.selectedUserId,
        startAt: this.startAt,
        finishAt: this.finishAt,
        tenantName: this.tenantName,
        token: this.token,
        selectedUser: this.users.find(u => u.user_id === this.selectedUserId)
      };

      const userName = this.operationData.selectedUser ? this.operationData.selectedUser.user_name : 'o usuário selecionado';
      this.close();

      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirmar Ação',
        text: `Você tem certeza que deseja desfazer todos os encerramentos de "${userName}" no período informado? Esta ação não pode ser desfeita.`,
        accept: this.undoFinishedTickets,
        cancel: this.reopenModal,
        acceptText: 'Sim, desfazer',
        cancelText: 'Cancelar'
      });
    },
    undoFinishedTickets() {
      if (!this.operationData) {
        console.error('Dados da operação não encontrados');
        return;
      }

      this.loading = true;
      this.result = null;

      this.$store.dispatch('undoFinishedTickets', {
        userId: this.operationData.selectedUserId,
        from: this.operationData.startAt,
        to: this.operationData.finishAt,
        tenantName: this.operationData.tenantName,
        token: this.operationData.token
      }).then(response => {
        this.result = response.data;
        this.loading = false;
        this.operationData = null;
        this.$vs.notify({
          color: 'success',
          title: 'Sucesso!',
          text: `${response.data.total_updated} atendimentos foram reabertos.`
        });
      }).catch(exception => {
        console.error(exception);
        this.loading = false;
        this.$vs.notify({
          color: 'danger',
          title: 'Erro!',
          text: 'Ocorreu um erro ao desfazer os encerramentos! Entre em contato com o time técnico.'
        });
      });
    },
  },
  watch: {
    isActive(value) {
      if (!value) {
        this.currentStep = 1;
        this.selectedUserId = null;
        this.startAt = '';
        this.finishAt = '';
        this.users = [];
        this.result = null;
        this.loading = false;
        this.loadingUsers = false;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.d-flex {
  display: flex;
}

.flex-column {
  flex-direction: column;
}

.justify-end {
  justify-content: flex-end;
}

.justify-center {
  justify-content: center;
}

.justify-between {
  justify-content: space-between;
}

.step-content {
  h4 {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }

  .step-description {
    font-size: 13px;
    color: #666;
    margin-bottom: 20px;
    line-height: 1.4;
  }
}

.period-summary {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 16px;

  p {
    font-size: 13px;
    color: #495057;
    margin: 0;
  }
}

.confirmation-summary {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 16px;

  .summary-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #e9ecef;

    &:last-child {
      border-bottom: none;
    }

    strong {
      font-weight: 600;
      color: #495057;
      font-size: 13px;
    }

    span {
      color: #6c757d;
      font-size: 13px;
    }
  }
}

.warning-box {
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 16px;

  p {
    font-size: 13px;
    color: #856404;
    margin: 0;
    font-weight: 500;
  }
}

.info-box {
  background: #e3f2fd;
  border-left: 4px solid #2196f3;
  border-radius: 4px;
  padding: 12px;

  p {
    font-size: 13px;
    color: #1976d2;
    margin: 0;
  }
}

.result-box {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;

  h4 {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
    border-bottom: 2px solid #ddd;
    padding-bottom: 4px;
  }

  p {
    font-size: 13px;
    color: #666;
    margin: 4px 0;
  }
}
</style>
